import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from '@app/store/session/session.service';
import { OpenService } from '@app/shared/services/open.service';
import { SendLeadRequest } from '@app-model/auction-lead';
import { Vehicle } from '@app-model/vehicle';
import { GaEventsService } from '@app-services/ga-events.service';
import { AdminService } from '@app/views/private/admin/services/admin.service';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { ElementRef } from '@angular/core';
import { BCrowValidators } from '@app-utils/bcrow-validators';

declare var grecaptcha: any;

@Component({
  selector: 'app-send-lead',
  templateUrl: './send-lead.component.html',
  styleUrls: ['./send-lead.component.scss']
})
export class SendLeadComponent implements OnInit, OnDestroy {
  @Input() vehicle: Vehicle;
  @Input() disableConfirmationModal: boolean;

  leadForm: FormGroup;
  leadSent = false;
  errorMessage: string;
  menuColorNotWhite = "#a6aaaf"
  recaptchaCompleted = false;
  recaptchaTouched = false;
  showForm = false;
  transactional: boolean;

  constructor(
    private fb: FormBuilder,
    private sessionService: SessionService,
    private adminService: AdminService,
    private openService: OpenService,
    private gaEventService: GaEventsService,
    private elRef: ElementRef
  ) { }

  ngOnInit() {

    this.openService.getLayoutVars().subscribe(layoutVars => {
      if (layoutVars) {
        MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
        this.menuColorNotWhite = MarketPlacePersonalizationUtils.getColorNotWhite(['offerColor', 'primaryColor', 'buttonColor'], layoutVars);
        this.transactional = layoutVars.transactional;
      }
    })
    this.leadForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      message: [''],
      ...(this.vehicle.leadGenerationCollectCpfCnpj && {
        cpfCnpj: ['', [Validators.required, BCrowValidators.validatorCpfCnpj]]
      })
    });

    this.loadUserInfo();
    this.loadRecaptchaScript();
  }

  ngOnDestroy() {
    // Verifica se o grecaptcha existe antes de tentar resetá-lo
    if (typeof grecaptcha !== 'undefined' && grecaptcha) {
      try {
        grecaptcha.reset();
      } catch (error) {
        console.warn('Erro ao resetar reCAPTCHA:', error);
      }
    }
    this.removeRecaptchaScript();
  }

  private removeRecaptchaScript() {
    const script = document.querySelector('script[src*="recaptcha"]');
    if (script) {
      script.remove();
    }
  }

  private loadRecaptchaScript() {
    // Verifica se o script do reCAPTCHA já foi carregado
    if (typeof grecaptcha !== 'undefined') {
      return;
    }

    // Cria e adiciona o script do reCAPTCHA dinamicamente
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=6Ldxo2QrAAAAAN4UXfIzrKF5VytYqEi-oWJ8rjDT';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  private verifyRecaptcha(token: string) {
    // Aqui você pode enviar o token para o backend para verificação
    // Por enquanto, apenas marcamos como completado
  }

  private loadUserInfo() {
    this.sessionService.getUser().subscribe(user => {

      if (user?.dealerId && user?.isDealer()) {
        this.adminService.getDealer(user.dealerId).subscribe(dealer => {
          let phoneNumber = '';
          if (dealer.phoneNumbers?.length > 0) {
            phoneNumber = dealer.phoneNumbers[0];
          }
          if (dealer.whatsappNumber) {
            phoneNumber = dealer.whatsappNumber;
          }
          this.leadForm.patchValue({
            name: dealer.name,
            email: dealer.email,
            phoneNumber
          });
        });
      } else if (user) {

        this.leadForm.patchValue({
          name: user.name,
          email: user.email
        });
      }
    });
  }

  sendLead() {
    this.leadForm.markAllAsTouched();
    this.recaptchaTouched = true;
    
    if (this.leadForm.disabled) return;

    if (this.leadForm.valid) {
      // Aguarda o carregamento do reCAPTCHA antes de executar
      this.executeRecaptcha();
    } else {
      this.errorMessage = 'Por favor, preencha todos os campos obrigatórios.';
    }
  }

  private executeRecaptcha() {
    // Verifica se o reCAPTCHA já foi carregado
    if (typeof grecaptcha !== 'undefined') {
      this.runRecaptcha();
    } else {
      // Se ainda não carregou, aguarda
      const checkRecaptcha = setInterval(() => {
        if (typeof grecaptcha !== 'undefined') {
          this.runRecaptcha();
          clearInterval(checkRecaptcha);
        }
      }, 100);
    }
  }

  private runRecaptcha() {
    grecaptcha.ready(() => {
      grecaptcha.execute('6Ldxo2QrAAAAAN4UXfIzrKF5VytYqEi-oWJ8rjDT', {action: 'send_lead'})
        .then((token: string) => {
          this.recaptchaCompleted = true;
          this.verifyRecaptcha(token);
          this.submitLead();
        })
        .catch((error: any) => {
          console.error('Erro no reCAPTCHA:', error);
          this.errorMessage = 'Erro na verificação de segurança. Por favor, tente novamente.';
        });
    });
  }

  private submitLead() {
    this.leadForm.disable();
    const request: SendLeadRequest = {
      auctionId: this.vehicle.id,
      ...this.leadForm.value
    };

    this.leadSent = true;
    this.openService.sendLead(request).subscribe(
      response => {
        if (response.success) {
          this.leadSent = true;
          this.errorMessage = null;

          this.gaEventService.fireEvent('Auction Lead', 'Envio', this.vehicle.id);
        } else {
          this.errorMessage = `Não foi possível enviar sua mensagem. Por favor, tente novamente: ${response.message}`;
          this.leadSent = false;
        }
        this.leadForm.enable();
      },
      error => {
        this.errorMessage = 'Erro de conexão. Por favor, tente novamente mais tarde.';
        this.leadSent = false;
        this.leadForm.enable();
      }
    );
  }
}
